// pages/Overview.js
import { navigateTo } from "../framework/router.ts";
import { OverviewHtml } from "../components/skins/overview.ts";
import { useState, resetCursor } from "../framework/useState.ts";
import { createSite, updateSite, fetchSites, fetchSiteWithData, fetchFleetOverview } from "../services/api.ts";
import { UISkinView } from "../components/views/UISkinView.ts";
import { Modal } from "../components/modal.ts";
import { globalState } from "../framework/globalState.ts";
import { formatSiteDate, formatSiteTime, resolveSiteTimezone } from "../utils/timezone.ts";
import {
  getOverviewWarmSnapshot,
  primeOverviewWarmSnapshot,
  startOverviewWarmPolling,
  subscribeOverviewWarmSnapshot,
  type OverviewWarmSnapshot,
} from "../services/overviewWarmCache.ts";

export default function Overview() {
  resetCursor();
  const template = document.createElement("template");
  template.innerHTML = new OverviewHtml().overviewskin().trim();
  const fragment = template.content;
  const siteGrid = fragment.querySelector("#siteGrid");
  const [filter, setFilter] = useState("");
  let sites = [];
  let siteClockInterval = null;
  let overviewWarmUnsubscribe = null;
  const fleetKpiBySite = {};

  /* ------------------ REGISTER CONFIG ------------------ */
  const REGISTER_MAP = {
    PV: "PV Power",
    BESS: "BESS SOC",
    Diesel: "Start Generator",
    GasTurbines: "Gas Turbine",
    Loads: "Load",
    Grid: "Grid Frequency"
  };
  const REGISTER_FALLBACK = {
    PV: "0.0 kW",
    BESS: "0.0 %",
    Diesel: "Off",
    GasTurbines: "N/A",
    Loads: "N/A",
    Grid: "N/A"
  };

  function stopSiteClockPolling() {
    if (siteClockInterval) {
      clearInterval(siteClockInterval);
      siteClockInterval = null;
    }
  }

  /* ------------------ HELPERS ------------------ */
  function displayRegister(value, fallback, unit = "") {
    if (value == null || value === "") {
      return `<span class="text-muted">${fallback}</span>`;
    }

    if (typeof value === "string") {
      return `<strong>${value}</strong>`;
    }

    if (isNaN(Number(value))) {
      return `<span class="text-muted">${fallback}</span>`;
    }

    const formatted = Number(value).toFixed(1);
    return `<strong>${formatted}</strong> ${unit}`.trim();
  }

  function getRegisterValue(field, siteId) {
    const kpi = fleetKpiBySite[siteId];
    if (!kpi) return { value: null, unit: "" };

    switch (field) {
      case "PV":
        return { value: kpi.pvActivePower.value, unit: kpi.pvActivePower.unit };
      case "BESS":
        return { value: kpi.bessSoc.value, unit: kpi.bessSoc.unit };
      case "Diesel": {
        const status = String(kpi.dgStatus || "unknown").toLowerCase();
        return { value: status === "running" ? "Running" : status === "stopped" ? "Stopped" : "Unknown", unit: "" };
      }
      case "GasTurbines": {
        const status = String(kpi.gasTurbineStatus || "").toLowerCase();
        if (!status) return { value: null, unit: "" };
        return {
          value: status === "running" ? "Running" : status === "stopped" ? "Stopped" : status === "unknown" ? "Unknown" : null,
          unit: "",
        };
      }
      case "Loads":
        return { value: kpi.totalLoadPower?.value ?? null, unit: kpi.totalLoadPower?.unit ?? "kW" };
      case "Grid":
        return { value: kpi.gridActivePower.value, unit: kpi.gridActivePower.unit };
      default:
        return { value: null, unit: "" };
    }
  }

  function renderSiteStatus(site, statusCell) {
    const status = (site.status || "offline").toLowerCase();
    const dotClass = status === "online" ? "online pulse" :
                    status === "maintenance" ? "maintenance" : "offline";
    const text = status === "maintenance" ? "Maintenance" :
                status === "online" ? "Online" : "Offline";

    // Preserve existing buttons by only updating the status indicator part
    let statusIndicator = statusCell.querySelector(".status-indicator");
    if (!statusIndicator) {
      statusIndicator = document.createElement("div");
      statusIndicator.className = "status-indicator";
      // Insert before any buttons
      statusCell.insertBefore(statusIndicator, statusCell.firstChild);
    }

    statusIndicator.innerHTML = `
      <span class="status-dot ${dotClass}"></span>
      ${text}
    `;
  }

  function updateRegisterCell(siteId, field) {
    const row = siteGrid.querySelector(`tr.site-data-row[data-site-id="${siteId}"]`);
    if (!row) return;
    const cell = row.querySelector(`.reg-${field}`);
    if (!cell) return;
    const { value, unit } = getRegisterValue(field, siteId);
    cell.innerHTML = displayRegister(value, REGISTER_FALLBACK[field], unit);
  }

  function applyOverviewSnapshot(snapshot: OverviewWarmSnapshot) {
    const currentUser = globalState.user as any || {};
    const allowedSiteIds = currentUser.isSuperAdmin ? null : new Set((currentUser.siteAccess || []).map((access: any) => access.siteId));

    const nextSites = (snapshot.sites || []).filter((site: any) => {
      if (site?.product !== 'vetra') return false;
      if (!allowedSiteIds) return true;
      return allowedSiteIds.has(site._id);
    });

    sites = nextSites;

    Object.keys(fleetKpiBySite).forEach((siteId) => delete fleetKpiBySite[siteId]);
    const bySiteId = Object.fromEntries((snapshot.fleetOverview || []).map((kpi: any) => [kpi.siteId, kpi]));
    sites.forEach((site: any) => {
      fleetKpiBySite[site._id] = bySiteId[site._id] || null;
    });

    renderSites();

    // Update register cells with actual data after rendering
    sites.forEach((site: any) => {
      Object.keys(REGISTER_MAP).forEach(field => {
        updateRegisterCell(site._id, field);
      });
    });
  }

  function updateSiteLocalTimes() {
    const now = new Date();

    siteGrid.querySelectorAll("tr.site-data-row").forEach((rowEl) => {
      const row = rowEl as HTMLTableRowElement;
      const siteId = row.dataset.siteId;
      if (!siteId) return;

      const site = sites.find((s) => s._id === siteId);
      const cell = row.querySelector(".site-local-time") as HTMLElement | null;
      if (!cell) return;

      const timezone = resolveSiteTimezone(site);
      cell.textContent = `${formatSiteDate(now, timezone)} ${formatSiteTime(now, timezone)}`;
      cell.title = timezone;
    });
  }

  /* ------------------ RENDER SITES ------------------ */
  async function renderSites() {
    const filteredSites = sites.filter(site =>
      site.name.toLowerCase().includes(filter.toLowerCase())
    );

    siteGrid.innerHTML = filteredSites.length === 0
      ? `<p>No sites available</p>`
      : "";

    if (!filteredSites.length) return;

    const card = document.createElement("div");
    card.className = "site-card table-responsive";
    const table = document.createElement("table");
    table.className = "site-overview-table";

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    ["Status", "Country", "Location", "Local Time", "NamePlate", "PV", "BESS", "Diesel Generator",
     "Gas Turbines", "Loads", "Grid", "Alerts", "Critical Alerts"]
      .forEach(text => {
        const th = document.createElement("th");
        th.textContent = text;
        headerRow.appendChild(th);
      });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");

    for (const site of filteredSites) {
      const row = document.createElement("tr");
      row.className = "site-data-row";
      row.dataset.siteId = site._id;

      // Status Cell with buttons
      const statusCell = document.createElement("td");
      
      renderSiteStatus(site, statusCell);

      // Maintenance Button
      const maintenanceBtn = document.createElement("button");
      maintenanceBtn.innerHTML = '<span class="iconify" data-icon="mdi:wrench"></span>';
      maintenanceBtn.className = "btn-maintenance-sm";
      maintenanceBtn.title = "Toggle Maintenance Mode";
      maintenanceBtn.onclick = async (e) => {
        e.stopPropagation();
        const newStatus = site.status === "maintenance" ? "offline" : "maintenance";
        if (confirm(`Set "${site.name}" to ${newStatus.toUpperCase()}?`)) {
          try {
            await updateSite(site._id, { status: newStatus });
            site.status = newStatus;
            renderSiteStatus(site, statusCell);
          } catch (err) {
            alert("Failed to update status");
          }
        }
      };
      statusCell.appendChild(maintenanceBtn);

      // EDIT BUTTON — CLEAN & DIRECT
      const editBtn = document.createElement("button");
      editBtn.innerHTML = '<span class="iconify" data-icon="mdi:pencil"></span>';
      editBtn.className = "btn-edit-sm";
      editBtn.title = "Edit Site";
      editBtn.style.marginLeft = "8px";
      editBtn.onclick = (e) => {
        e.stopPropagation();
        siteModal.open(site);
      };
      statusCell.appendChild(editBtn);
      // MAP BUTTON
      if (site.location?.latitude && site.location?.longitude) {
        const mapBtn = document.createElement("button");
        mapBtn.innerHTML = '<span class="iconify" data-icon="mdi:map-marker-radius"></span>';
        mapBtn.className = "btn-edit-sm";
        mapBtn.title = "View on Map";
        mapBtn.style.marginLeft = "8px";
        mapBtn.style.color = "#1a73e8";
        mapBtn.onclick = (e) => {
          e.stopPropagation();
          openMapDrawer(site);
        };
        statusCell.appendChild(mapBtn);
      }

      row.appendChild(statusCell);

      // Fixed Info
      const countryTd = document.createElement("td");
      countryTd.textContent = site.location?.country || "—";
      row.appendChild(countryTd);

      const locationTd = document.createElement("td");
      locationTd.textContent = site.location?.state || site.location?.address || "—";
      row.appendChild(locationTd);

      const localTimeTd = document.createElement("td");
      localTimeTd.className = "site-local-time";
      localTimeTd.textContent = "—";
      row.appendChild(localTimeTd);

      const nameplateTd = document.createElement("td");
      nameplateTd.textContent = site.capacity ? site.capacity : "—";
      row.appendChild(nameplateTd);

      // Register Cells
      Object.keys(REGISTER_MAP).forEach(field => {
        const cell = document.createElement("td");
        cell.className = `reg-${field}`;
        cell.innerHTML = `<span class="text-muted">Loading...</span>`;
        row.appendChild(cell);
      });

      // Alerts
      ["0", "0"].forEach(val => {
        const td = document.createElement("td");
        td.textContent = val;
        row.appendChild(td);
      });

      tbody.appendChild(row);
    }

    table.appendChild(tbody);
    card.appendChild(table);
    siteGrid.appendChild(card);

    bindSiteCardEvents();
    updateSiteLocalTimes();
    stopSiteClockPolling();
    siteClockInterval = setInterval(updateSiteLocalTimes, 1000);
  }

  /* ------------------ NAVIGATE TO DASHBOARD ON ROW CLICK ------------------ */
  function bindSiteCardEvents() {
    siteGrid.querySelectorAll("tr.site-data-row").forEach(row => {
      row.addEventListener("click", async (e) => {
        // Allow buttons to work without triggering navigation
        if ((e.target as Element).closest("button")) return;

        const siteId = (row as HTMLElement).dataset.siteId;
        const site = sites.find(s => s._id === siteId);
        if (!site) return;

        (row as HTMLElement).style.opacity = "0.6";
        (row as HTMLElement).style.pointerEvents = "none";

        try {
          const siteWithData = await fetchSiteWithData(siteId);
          globalState.currentSite = site;
          globalState.devices = siteWithData.devices || [];
          globalState.registers = siteWithData.devices.flatMap((d: any) =>
            (d.registers || []).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
          );
          globalState.currentSiteWithData = { ...siteWithData.site, devices: siteWithData.devices };
          navigateTo(`/dashboard?site=${siteId}`);
        } catch (err) {
          console.error(err);
          alert("Failed to load site data");
          (row as HTMLElement).style.opacity = "1";
          (row as HTMLElement).style.pointerEvents = "auto";
        }
      });
    });
  }

  async function refreshFleetOverviewData() {
    try {
      const snapshot = await primeOverviewWarmSnapshot();
      applyOverviewSnapshot(snapshot);
    } catch (err) {
      console.error('Failed to load fleet overview data:', err);
    }
  }

  /* ------------------ INIT ------------------ */
  async function init() {
    try {
      const cachedSnapshot = getOverviewWarmSnapshot();
      if (cachedSnapshot) {
        applyOverviewSnapshot(cachedSnapshot);
      } else {
        siteGrid.innerHTML = `<p><small>Loading sites...</small></p>`;
        await refreshFleetOverviewData();
      }

      startOverviewWarmPolling();
      overviewWarmUnsubscribe = subscribeOverviewWarmSnapshot((snapshot) => {
        applyOverviewSnapshot(snapshot);
      });
    } catch (err) {
      siteGrid.innerHTML = `<p class="error">Failed to load sites</p>`;
      console.error(err);
    }
  }

  init();

  /* ------------------ FILTER ------------------ */
  fragment.querySelector("#filterInput")?.addEventListener("input", e => {
    setFilter((e.target as HTMLInputElement).value);
    renderSites();
  });

  /* ------------------ ADD & EDIT SITE MODAL — NOW BEAUTIFUL & CONSISTENT ------------------ */
  class SiteManagerModal {
    modal: InstanceType<typeof Modal>;

    constructor() {
      this.modal = new Modal();
    }

    open(site = null) {
      const isEdit = !!site;

      const countryOptions = [
        "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt", "Morocco", "Algeria",
        "Uganda", "Tanzania", "Rwanda", "Ethiopia", "Senegal", "Ivory Coast", "Cameroon"
      ].sort().map(c => 
        `<option value="${c}" ${site?.location?.country === c ? 'selected' : ''}>${c}</option>`
      ).join('');

      const html = `
        <div class="modal-content site-manager">
          <div class="modal-header">
            <h3>${isEdit ? 'Edit Site' : 'Add New Site'}</h3>
          </div>
          <div class="modal-body">
            <form id="site-form">
              ${isEdit ? `<input type="hidden" name="_id" value="${site._id}">` : ''}
              <div class="form-grid">
                <div class="full-width">
                  <label>Site Name *</label>
                  <input name="name" value="${site?.name || ''}" required maxlength="100">
                </div>
                <div>
                  <label>Country *</label>
                  <select name="country" required>
                    <option value="">-- Select Country --</option>
                    ${countryOptions}
                  </select>
                </div>
                <div class="full-width">
                  <label>Location Details</label>
                  <div class="form-grid" style="margin-top: 0.5rem; gap: 0.75rem;">
                    <div>
                      <label>State / Region *</label>
                      <input name="location.state" value="${site?.location?.state || ''}" required placeholder="e.g. Lagos, Nairobi, Gauteng">
                    </div>
                    <div>
                      <label>City / Town</label>
                      <input name="location.address" value="${site?.location?.address || ''}" placeholder="e.g. Ikeja, Westlands">
                    </div>
                    <div>
                      <label>Latitude</label>
                      <input name="location.latitude" type="number" step="any" value="${site?.location?.latitude || ''}" placeholder="e.g. 6.5244">
                    </div>
                    <div>
                      <label>Longitude</label>
                      <input name="location.longitude" type="number" step="any" value="${site?.location?.longitude || ''}" placeholder="e.g. 3.3792">
                    </div>
                  </div>
                </div>
                <div>
                  <label>Nameplate Capacity (MW) *</label>
                  <input name="capacity" value="${site?.capacity || ''}" required>
                </div>
                <div class="full-width">
                  <label>Description</label>
                  <textarea name="description" rows="3">${site?.description || ''}</textarea>
                </div>
              </div>
              <div class="form-actions" style="margin-top: 1.5rem;">
                <button type="submit" class="btn-primary">
                  ${isEdit ? 'Update Site' : 'Create Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      `;

      this.modal.open(html);

      const form = document.querySelector('#site-form') as HTMLFormElement | null;
      if (!form) return;
      form.onsubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const data: any = {
          product: 'vetra'  // Identify this site as a Vetra product
        };

        // Handle nested location fields
        data.location = {};
        ['state', 'address', 'latitude', 'longitude'].forEach(field => {
          const key = `location.${field}`;
          if (formData.has(key)) {
            const val = formData.get(key);
            data.location[field] = field === 'latitude' || field === 'longitude' 
              ? (val === '' ? null : Number(val)) 
              : (val || null);
          }
        });

        // Add top-level fields
        data.name = formData.get('name');
        data.location.country = formData.get('country');
        data.capacity = formData.get('capacity');
        data.description = formData.get('description') || null;

        // Clean up empty location object
        if (Object.values(data.location).every(v => v === null)) {
          delete data.location;
        }

        try {
          if (isEdit) {
            await updateSite(site._id, data);
            alert('Site updated successfully!');
          } else {
            await createSite(data);
            alert('Site created successfully!');
          }
          this.modal.close();
          await init();
        } catch (err) {
          console.error(err);
          alert('Error: ' + (err.message || 'Failed to save site'));
        }
      };
    }
  }

  // 
  const siteModal = new SiteManagerModal();

  // Add Site Button
  fragment.querySelector("#addSiteBtn")?.addEventListener("click", () => {
    siteModal.open();
  });

    /* ------------------ MAP DRAWER — PERFECT, NO API, NO ERRORS ------------------ */
  let mapDrawer = null;
  let mapOverlay = null;

  function openMapDrawer(site) {
    if (!site.location?.latitude || !site.location?.longitude) {
      alert("No GPS coordinates set for this site.");
      return;
    }

    const lat = site.location.latitude;
    const lng = site.location.longitude;
    const name = site.name;
    const state = site.location.state || site.location.address || "Site Location";

    // Create overlay
    if (!mapOverlay) {
      mapOverlay = document.createElement("div");
      mapOverlay.className = "map-overlay";
      mapOverlay.onclick = closeMapDrawer;
      document.body.appendChild(mapOverlay);
    }

    // Create drawer
    if (!mapDrawer) {
      mapDrawer = document.createElement("div");
      mapDrawer.className = "map-drawer";
      mapDrawer.innerHTML = `
        <div class="map-drawer-header">
          <div>
            <strong id="map-title">${name}</strong><br>
            <small id="map-subtitle">${state}</small>
          </div>
          <button class="map-drawer-close">×</button>
        </div>
        <div style="flex: 1; position: relative;">
          <iframe 
            id="google-map-iframe"
            width="100%" 
            height="100%" 
            style="border:0;" 
            loading="lazy" 
            allowfullscreen 
            referrerpolicy="no-referrer-when-downgrade"
            src="">
          </iframe>
        </div>
        <div style="padding: 1rem; background: #1a1a2e; color: white; text-align: center;">
          <small>
            Lat: ${lat.toFixed(6)} | Lng: ${lng.toFixed(6)}
          </small>
        </div>
      `;
      mapDrawer.querySelector(".map-drawer-close").onclick = closeMapDrawer;
      document.body.appendChild(mapDrawer);
    } else {
      mapDrawer.querySelector("#map-title").textContent = name;
      mapDrawer.querySelector("#map-subtitle").textContent = state;
    }

    // BEST GOOGLE MAPS EMBED URL — 2025 WORKING
    const encodedName = encodeURIComponent(name);
    const iframe = mapDrawer.querySelector("#google-map-iframe");
    // Make the marker red and labeled (OSM supports this)
const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.008},${lat-0.008},${lng+0.008},${lat+0.008}&layer=mapnik&marker=${lat},${lng},red`;
    iframe.src = mapUrl;

    // Open drawer
    mapDrawer.classList.add("open");
    mapOverlay.classList.add("open");
  }

  function closeMapDrawer() {
    if (mapDrawer) mapDrawer.classList.remove("open");
    if (mapOverlay) mapOverlay.classList.remove("open");
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeMapDrawer();
  });

  const cleanup = () => {
    if (typeof overviewWarmUnsubscribe === 'function') {
      overviewWarmUnsubscribe();
      overviewWarmUnsubscribe = null;
    }
    stopSiteClockPolling();
  };

  const rootElement = fragment.firstElementChild as HTMLElement | null;
  if (rootElement) {
    (rootElement as any).cleanup = cleanup;
  }

  return fragment;
}